/**
 * PATH: packages/job-hub/src/jobs/qa/checklistTable.ts
 * FILE_ID: JOBHUB-QA-TABLE-HELPERS-001
 * PURPOSE: Pure I->O helpers for the editable QA checklist table — row reorder
 *          maths and the sort_order payload the reorder endpoint expects.
 * WHY:     The "excel table with cells like clay" editor moves/edits rows before
 *          the QA runs. Keeping the array maths pure lets it be unit-tested
 *          without React and keeps QaChecklistTable.tsx declarative.
 * RELATED:
 * - qa/api/views/instance.py (ItemInstanceViewSet.reorder consumes the payload)
 * - ./qaApi.ts (reorderItems sends it)
 */

/**
 * Move the row at ``index`` by ``dir`` (-1 up, +1 down). Returns a NEW array;
 * out-of-bounds moves are a no-op (return the same order) so callers can wire
 * up/down buttons without bounds-checking at the edges.
 */
export function moveRow<T>(rows: readonly T[], index: number, dir: -1 | 1): T[] {
  const next = [...rows]
  const target = index + dir
  if (index < 0 || index >= next.length || target < 0 || target >= next.length) {
    return next
  }
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}

/**
 * Move the row at ``index`` to swap with its nearest SIBLING (same parent) in
 * the given direction, skipping over rows of other parents. Returns a NEW array;
 * a no-op (same order) when there's no sibling that way. Keeps sub-rows ordering
 * within their group without disturbing other groups. Pure I->O.
 */
export function moveSibling<T extends { parent_item?: string | null }>(
  rows: readonly T[],
  index: number,
  dir: -1 | 1,
): T[] {
  const next = [...rows]
  if (index < 0 || index >= next.length) return next
  const parent = next[index].parent_item ?? null
  let target = index + dir
  while (target >= 0 && target < next.length) {
    if ((next[target].parent_item ?? null) === parent) break
    target += dir
  }
  if (target < 0 || target >= next.length) return next
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}

/** Map an ordered list of item-instance ids to the reorder endpoint payload. */
export function toSortOrderPayload(orderedIds: readonly string[]): { id: string; sort_order: number }[] {
  return orderedIds.map((id, sort_order) => ({ id, sort_order }))
}

/** A row plus its nesting depth, for indented tree rendering. */
export interface QaRowNode<T> {
  item: T
  depth: number
  /** True when an earlier row shares this row's parent (move-up enabled). */
  hasPrevSibling: boolean
  /** True when a later row shares this row's parent (move-down enabled). */
  hasNextSibling: boolean
}

/**
 * Order rows for display as a tree: each top-level row (no/again-missing parent)
 * in its given order, immediately followed by its descendants depth-first. Rows
 * whose ``parent_item`` isn't present are treated as top-level so nothing is ever
 * hidden, and a cycle can't loop forever (visited guard). Pure I->O.
 */
export function buildRowTree<T extends { id: string; parent_item?: string | null }>(
  rows: readonly T[],
): QaRowNode<T>[] {
  const ids = new Set(rows.map((r) => r.id))
  const childrenByParent = new Map<string, T[]>()
  const roots: T[] = []
  for (const r of rows) {
    const parent = r.parent_item
    if (parent && ids.has(parent) && parent !== r.id) {
      const list = childrenByParent.get(parent)
      if (list) list.push(r)
      else childrenByParent.set(parent, [r])
    } else {
      roots.push(r)
    }
  }
  const siblingFlags = (group: readonly T[], i: number) => ({
    hasPrevSibling: i > 0,
    hasNextSibling: i < group.length - 1,
  })
  const out: QaRowNode<T>[] = []
  const seen = new Set<string>()
  const visit = (group: readonly T[]) => {
    group.forEach((node, i) => {
      if (seen.has(node.id)) return
      seen.add(node.id)
      // depth is derived from the call stack via a wrapper below
      out.push({ item: node, depth: depthStack, ...siblingFlags(group, i) })
      depthStack += 1
      visit(childrenByParent.get(node.id) ?? [])
      depthStack -= 1
    })
  }
  let depthStack = 0
  visit(roots)
  for (const r of rows) {
    if (!seen.has(r.id)) out.push({ item: r, depth: 0, hasPrevSibling: false, hasNextSibling: false })
  }
  return out
}

/** Distinct, non-empty product names across rows (for AI per-product generate). */
export function distinctProducts<T extends { product?: string }>(rows: readonly T[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const r of rows) {
    const p = (r.product ?? '').trim()
    if (p && !seen.has(p.toLowerCase())) {
      seen.add(p.toLowerCase())
      out.push(p)
    }
  }
  return out
}

/**
 * Decide what a cell edit commits. Returns the trimmed value when it changed and
 * is allowed; ``null`` means "no change / revert" (e.g. a blank title). Pure so
 * the table can guard empty required cells consistently.
 */
export function commitCell(previous: string, draft: string, { allowEmpty }: { allowEmpty: boolean }): string | null {
  const trimmed = draft.trim()
  if (!allowEmpty && trimmed === '') return null
  if (trimmed === (previous ?? '').trim()) return null
  return trimmed
}

/**
 * Insert ``newId`` into ``ids`` at ``index`` (clamped to the array bounds),
 * de-duping first so a freshly-appended row id ends up exactly where the
 * operator dropped it. Pure — the "insert above/below" + "duplicate" context
 * actions append via the API then reorder to this target order.
 */
export function insertIdAt(ids: readonly string[], newId: string, index: number): string[] {
  const without = ids.filter((id) => id !== newId)
  const clamped = Math.max(0, Math.min(Math.trunc(index), without.length))
  without.splice(clamped, 0, newId)
  return without
}

// --- resizable column widths (the "increase cell width" ask) --------------

/** The operator-resizable text columns of the checklist table. */
export interface QaColumnWidths {
  item: number
  product: number
  requirement: number
  acceptance: number
}

export const QA_COLUMN_MIN_WIDTH = 100
export const QA_COLUMN_MAX_WIDTH = 760

export const DEFAULT_QA_COLUMN_WIDTHS: QaColumnWidths = {
  item: 220,
  product: 170,
  requirement: 280,
  acceptance: 240,
}

/** Clamp a dragged column width into sane bounds (NaN-safe). Pure I->O. */
export function clampColumnWidth(
  width: number,
  min: number = QA_COLUMN_MIN_WIDTH,
  max: number = QA_COLUMN_MAX_WIDTH,
): number {
  if (!Number.isFinite(width)) return min
  return Math.max(min, Math.min(max, Math.round(width)))
}

/**
 * Parse persisted column widths (localStorage JSON) into a complete, clamped
 * ``QaColumnWidths``, falling back to defaults for anything missing or invalid.
 * Pure so persistence round-trips can be unit-tested without the DOM.
 */
export function parseColumnWidths(raw: string | null): QaColumnWidths {
  if (!raw) return { ...DEFAULT_QA_COLUMN_WIDTHS }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { ...DEFAULT_QA_COLUMN_WIDTHS }
  }
  if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_QA_COLUMN_WIDTHS }
  const obj = parsed as Record<string, unknown>
  const pick = (key: keyof QaColumnWidths): number => {
    const v = obj[key]
    return typeof v === 'number' && Number.isFinite(v)
      ? clampColumnWidth(v)
      : DEFAULT_QA_COLUMN_WIDTHS[key]
  }
  return {
    item: pick('item'),
    product: pick('product'),
    requirement: pick('requirement'),
    acceptance: pick('acceptance'),
  }
}
